import { useEffect } from "react";
import { Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";

import { ILLUSTRATED_GUIDE_URL } from "./content/adminGuide";

import AppShell from "./components/layout/AppShell";
import RequireRole from "./auth/RequireRole";
import { homePathForUser, useAuth } from "./auth/AuthContext";
import LoginPage from "./pages/LoginPage";
import ChooseAccountModePage from "./pages/ChooseAccountModePage";
import HomeSuperadminPage from "./pages/HomeSuperadminPage";
import HomeClubPage from "./pages/HomeClubPage";
import ClubActivatePage from "./pages/club/ClubActivatePage";
import ClubInvitePage from "./pages/ClubInvitePage";
import SuperadminClubsPage from "./pages/SuperadminClubsPage";
import SuperadminMembershipIncomePage from "./pages/SuperadminMembershipIncomePage";
import SuperadminTournamentsPage from "./pages/SuperadminTournamentsPage";
import SuperadminPlatformFeesPage from "./pages/SuperadminPlatformFeesPage";
import SuperadminOrganizerFeesPage from "./pages/SuperadminOrganizerFeesPage";
import SuperadminSupportTicketsPage from "./pages/SuperadminSupportTicketsPage";
import SuperadminDemoRequestsPage from "./pages/SuperadminDemoRequestsPage";
import SuperadminRegistrationDiagnosticsPage from "./pages/SuperadminRegistrationDiagnosticsPage";
import HomeAdminPage from "./pages/HomeAdminPage";
import HomeJugadorPage from "./pages/HomeJugadorPage";
import AdminTournamentsListPage from "./pages/AdminTournamentsListPage";
import AdminTournamentNewPage from "./pages/AdminTournamentNewPage";
import AdminTournamentEditPage from "./pages/AdminTournamentEditPage";
import AdminTournamentSetupFormatPage from "./pages/AdminTournamentSetupFormatPage";
import AdminTournamentSetupCallPage from "./pages/AdminTournamentSetupCallPage";
import AdminTournamentDetailPage from "./pages/AdminTournamentDetailPage";
import AdminTournamentHomePage from "./pages/AdminTournamentHomePage";
import AdminPlayerProfilePage from "./pages/AdminPlayerProfilePage";
import AdminPlayersListPage from "./pages/AdminPlayersListPage";
import AdminTeamPage from "./pages/AdminTeamPage";
import AdminClubCalendarPage from "./pages/AdminClubCalendarPage";
import AdminTournamentPlayersPage from "./pages/AdminTournamentPlayersPage";
import AdminTournamentPlayerEnrollPage from "./pages/AdminTournamentPlayerEnrollPage";
import AdminTournamentPlayerDetailPage from "./pages/AdminTournamentPlayerDetailPage";
import AdminTournamentRankingPage from "./pages/AdminTournamentRankingPage";
import AdminTournamentGroupsPage from "./pages/AdminTournamentGroupsPage";
import AdminTournamentRoundsPage from "./pages/AdminTournamentRoundsPage";
import AdminTournamentSchedulePage from "./pages/AdminTournamentSchedulePage";
import AdminTournamentStandingsPage from "./pages/AdminTournamentStandingsPage";
import AdminTournamentCommunicationsPage from "./pages/AdminTournamentCommunicationsPage";
import AdminTournamentRulesPage from "./pages/AdminTournamentRulesPage";
import AdminTournamentResultReportsPage from "./pages/AdminTournamentResultReportsPage";
import AdminTournamentRefereesPage from "./pages/AdminTournamentRefereesPage";
import RefereeMatchesPage from "./pages/RefereeMatchesPage";
import AdminTournamentRegistrationsPage from "./pages/AdminTournamentRegistrationsPage";
import AdminTournamentTeamPage from "./pages/AdminTournamentTeamPage";
import AdminGuidePage from "./pages/AdminGuidePage";
import AdminChargeCreatePage from "./pages/AdminChargeCreatePage";
import AdminDocumentViewerPage from "./pages/AdminDocumentViewerPage";
import TournamentSetupGuard from "./components/TournamentSetupGuard";
import PlayerTournamentHomePage from "./pages/PlayerTournamentHomePage";
import PlayerTournamentBracketPage from "./pages/PlayerTournamentBracketPage";
import PlayerTournamentDrawCeremonyPage from "./pages/PlayerTournamentDrawCeremonyPage";
import PlayerTournamentGamesPage from "./pages/PlayerTournamentGamesPage";
import PlayerRankingPage from "./pages/PlayerRankingPage";
import PlayerStandingsPage from "./pages/PlayerStandingsPage";
import PlayerCommunicationsPage from "./pages/PlayerCommunicationsPage";
import PlayerTournamentCredentialPage from "./pages/PlayerTournamentCredentialPage";
import PlayerClubPage from "./pages/PlayerClubPage";
import PlayerClubReservationsPage from "./pages/PlayerClubReservationsPage";
import PlayerProfilePage from "./pages/PlayerProfilePage";
import PlayerHistoricalResultsPage from "./pages/PlayerHistoricalResultsPage";
import PlayerFriendsPage from "./pages/PlayerFriendsPage";
import PlayerTournamentsPage from "./pages/PlayerTournamentsPage";
import PlayerResultReportsPage from "./pages/PlayerResultReportsPage";
import PlayerTournamentPlayersPage from "./pages/PlayerTournamentPlayersPage";
import PlayerRulesPage from "./pages/PlayerRulesPage";
import PlayerNotificationsPage from "./pages/PlayerNotificationsPage";
import AdminNotificationsPage from "./pages/AdminNotificationsPage";
import AdminPaymentsPage from "./pages/AdminPaymentsPage";
import AdminFinanceDashboardPage from "./pages/AdminFinanceDashboardPage";
import Torneos123LandingPage from "./pages/Torneos123LandingPage";
import MultisportHubPage from "./pages/MultisportHubPage";
import PublicRankingPage from "./pages/PublicRankingPage";
import AdminRankingSystemsPage from "./pages/AdminRankingSystemsPage";
import AdminRankingHubPage from "./pages/AdminRankingHubPage";
import AdminCustomRankingPage from "./pages/AdminCustomRankingPage";
import PublicTournamentRegistrationPage from "./pages/PublicTournamentRegistrationPage";
import PublicOrderPaymentPage from "./pages/PublicOrderPaymentPage";
import PublicCredentialProfilePage from "./pages/PublicCredentialProfilePage";
import RecoverPasswordPage from "./pages/RecoverPasswordPage";
import CreatePasswordPage from "./pages/CreatePasswordPage";
import NotFoundPage from "./pages/NotFoundPage";

function isTorneos123Host() {
  return window.location.hostname === "torneos123.com" || window.location.hostname === "www.torneos123.com";
}

function RootRedirect() {
  const { status, user } = useAuth();

  if (isTorneos123Host()) {
    return <Torneos123LandingPage />;
  }

  if (status === "loading") {
    return (
      <section className="card">
        <p className="muted">Cargando sesión…</p>
      </section>
    );
  }
  if (status === "authenticated" && user) {
    return <Navigate to={homePathForUser(user)} replace />;
  }
  return <Navigate to="/login" replace />;
}

function TournamentAliasRedirect({ target }: { target: "schedule" | "communications" }) {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/admin/tournaments/${id}/${target}`} replace />;
}

/** Alias públicos de torneos123.com (/pricing, /clubes, /demo): redirigen a
 * /organizador conservando ?utm_… para que el formulario de demo sepa de
 * dónde llegó el organizador. */
function PublicAliasRedirect({ hash }: { hash?: string }) {
  const { search } = useLocation();
  return <Navigate to={{ pathname: "/organizador", search, hash }} replace />;
}

function IllustratedGuideRedirect() {
  useEffect(() => {
    window.location.replace(ILLUSTRATED_GUIDE_URL);
  }, []);
  return null;
}

export default function App() {
  return (
    <Routes>
      <Route element={<AppShell />}>
        <Route path="/" element={<RootRedirect />} />
        <Route path="/torneos123" element={<Torneos123LandingPage />} />
        <Route path="/torneos" element={<Torneos123LandingPage page="tournaments" />} />
        <Route path="/organizador" element={<Torneos123LandingPage page="organizer" />} />
        <Route path="/jugador" element={<Torneos123LandingPage page="player" />} />
        <Route path="/aviso-de-privacidad" element={<Torneos123LandingPage page="privacy" />} />
        <Route path="/pricing" element={<PublicAliasRedirect hash="#precio" />} />
        {/* El módulo de clubes está congelado: /clubes lleva a Organizadores. */}
        <Route path="/clubes" element={<PublicAliasRedirect />} />
        <Route path="/demo" element={<PublicAliasRedirect hash="#demo" />} />
        <Route path="/multisport" element={<MultisportHubPage />} />
        <Route path="/rankings/:slug" element={<PublicRankingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/recuperar" element={<RecoverPasswordPage />} />
        <Route path="/restablecer/:resetToken" element={<CreatePasswordPage mode="reset" />} />
        <Route
          path="/cuenta/contrasena"
          element={
            <RequireRole roles={["superadmin", "admin", "player", "club"]}>
              <CreatePasswordPage mode="self" />
            </RequireRole>
          }
        />
        <Route path="/guia" element={<AdminGuidePage publicMode />} />
        {/* Ruta amigable hacia la guía ilustrada estática: el SPA fallback
            se tragaría /guia-admin, así que se redirige al .html real. */}
        <Route path="/guia-admin" element={<IllustratedGuideRedirect />} />
        <Route path="/inscripcion/:slug" element={<PublicTournamentRegistrationPage />} />
        <Route path="/inscripcion/:slug/orden/:orderToken" element={<PublicOrderPaymentPage />} />
        <Route path="/credencial/:slug/:participantId" element={<PublicCredentialProfilePage />} />
        <Route path="/club-invite/:slug/:token" element={<ClubInvitePage />} />
        <Route
          path="/superadmin"
          element={
            <RequireRole roles={["superadmin"]}>
              <HomeSuperadminPage />
            </RequireRole>
          }
        />
        <Route
          path="/admin/rankings"
          element={
            <RequireRole roles={["admin", "superadmin"]}>
              <AdminRankingHubPage />
            </RequireRole>
          }
        />
        <Route
          path="/admin/rankings/propio/nuevo"
          element={
            <RequireRole roles={["admin", "superadmin"]}>
              <AdminCustomRankingPage />
            </RequireRole>
          }
        />
        <Route
          path="/admin/rankings/propio/:systemId"
          element={
            <RequireRole roles={["admin", "superadmin"]}>
              <AdminCustomRankingPage />
            </RequireRole>
          }
        />
        <Route
          path="/superadmin/fees"
          element={
            <RequireRole roles={["superadmin"]}>
              <SuperadminPlatformFeesPage />
            </RequireRole>
          }
        />
        <Route
          path="/superadmin/fees-organizadores"
          element={
            <RequireRole roles={["superadmin"]}>
              <SuperadminOrganizerFeesPage />
            </RequireRole>
          }
        />
        <Route
          path="/superadmin/tournaments"
          element={
            <RequireRole roles={["superadmin"]}>
              <SuperadminTournamentsPage />
            </RequireRole>
          }
        />
        <Route
          path="/superadmin/membresias"
          element={
            <RequireRole roles={["superadmin"]}>
              <SuperadminMembershipIncomePage />
            </RequireRole>
          }
        />
        <Route
          path="/superadmin/clubs"
          element={
            <RequireRole roles={["superadmin"]}>
              <SuperadminClubsPage />
            </RequireRole>
          }
        />
        <Route
          path="/superadmin/support-tickets"
          element={
            <RequireRole roles={["superadmin"]}>
              <SuperadminSupportTicketsPage />
            </RequireRole>
          }
        />
        <Route
          path="/superadmin/intentos-inscripcion"
          element={
            <RequireRole roles={["superadmin"]}>
              <SuperadminRegistrationDiagnosticsPage />
            </RequireRole>
          }
        />
        <Route
          path="/superadmin/demo-requests"
          element={
            <RequireRole roles={["superadmin"]}>
              <SuperadminDemoRequestsPage />
            </RequireRole>
          }
        />
        <Route
          path="/superadmin/rankings"
          element={
            <RequireRole roles={["superadmin"]}>
              <AdminRankingSystemsPage />
            </RequireRole>
          }
        />
        <Route
          path="/club/activar"
          element={
            <RequireRole roles={["admin", "superadmin"]}>
              <ClubActivatePage />
            </RequireRole>
          }
        />
        <Route
          path="/club"
          element={
            <RequireRole roles={["club"]}>
              <HomeClubPage />
            </RequireRole>
          }
        />
        <Route
          path="/admin"
          element={
            <RequireRole roles={["admin", "superadmin"]}>
              <HomeAdminPage />
            </RequireRole>
          }
        />
        <Route
          path="/admin/documento"
          element={
            <RequireRole roles={["admin", "superadmin"]}>
              <AdminDocumentViewerPage />
            </RequireRole>
          }
        />
        <Route
          path="/admin/payments"
          element={
            <RequireRole roles={["admin", "superadmin"]}>
              <AdminPaymentsPage />
            </RequireRole>
          }
        />
        <Route
          path="/admin/payments/mp-return"
          element={
            <RequireRole roles={["admin", "superadmin"]}>
              <AdminPaymentsPage />
            </RequireRole>
          }
        />
        <Route
          path="/admin/finance"
          element={
            <RequireRole roles={["admin", "superadmin"]}>
              <AdminFinanceDashboardPage />
            </RequireRole>
          }
        />
        <Route
          path="/admin/tournaments"
          element={
            <RequireRole roles={["admin", "superadmin"]}>
              <AdminTournamentsListPage />
            </RequireRole>
          }
        />
        <Route
          path="/admin/tournaments/new"
          element={
            <RequireRole roles={["admin", "superadmin"]}>
              <AdminTournamentNewPage />
            </RequireRole>
          }
        />
        <Route
          path="/admin/tournaments/:id/edit"
          element={
            <RequireRole roles={["admin", "superadmin"]}>
              <TournamentSetupGuard setupOnly>
                <AdminTournamentEditPage />
              </TournamentSetupGuard>
            </RequireRole>
          }
        />
        <Route
          path="/admin/tournaments/:id/setup/call"
          element={
            <RequireRole roles={["admin", "superadmin"]}>
              <TournamentSetupGuard setupOnly>
                <AdminTournamentSetupCallPage />
              </TournamentSetupGuard>
            </RequireRole>
          }
        />
        <Route
          path="/admin/tournaments/:id/setup/rules"
          element={
            <RequireRole roles={["admin", "superadmin"]}>
              <TournamentSetupGuard setupOnly>
                <AdminTournamentRulesPage setupMode />
              </TournamentSetupGuard>
            </RequireRole>
          }
        />
        <Route
          path="/admin/tournaments/:id/setup/registrations"
          element={
            <RequireRole roles={["admin", "superadmin"]}>
              <TournamentSetupGuard setupOnly>
                <AdminTournamentRegistrationsPage setupMode />
              </TournamentSetupGuard>
            </RequireRole>
          }
        />
        <Route
          path="/admin/tournaments/:id/setup/format"
          element={
            <RequireRole roles={["admin", "superadmin"]}>
              <TournamentSetupGuard setupOnly>
                <AdminTournamentSetupFormatPage />
              </TournamentSetupGuard>
            </RequireRole>
          }
        />
        <Route
          path="/admin/tournaments/:id/setup/players"
          element={
            <RequireRole roles={["admin", "superadmin"]}>
              <TournamentSetupGuard setupOnly>
                <AdminTournamentPlayersPage setupMode />
              </TournamentSetupGuard>
            </RequireRole>
          }
        />
        <Route
          path="/admin/tournaments/:id/setup/players/new"
          element={
            <RequireRole roles={["admin", "superadmin"]}>
              <TournamentSetupGuard setupOnly>
                <AdminTournamentPlayerEnrollPage setupMode />
              </TournamentSetupGuard>
            </RequireRole>
          }
        />
        <Route
          path="/admin/tournaments/:id/setup/ranking"
          element={
            <RequireRole roles={["admin", "superadmin"]}>
              <TournamentSetupGuard setupOnly>
                <AdminTournamentRankingPage />
              </TournamentSetupGuard>
            </RequireRole>
          }
        />
        <Route
          path="/admin/tournaments/:id/setup/groups"
          element={
            <RequireRole roles={["admin", "superadmin"]}>
              <TournamentSetupGuard setupOnly>
                <AdminTournamentGroupsPage setupMode />
              </TournamentSetupGuard>
            </RequireRole>
          }
        />
        <Route
          path="/admin/tournaments/:id/setup/rounds"
          element={
            <RequireRole roles={["admin", "superadmin"]}>
              <TournamentSetupGuard setupOnly>
                <AdminTournamentRoundsPage setupMode />
              </TournamentSetupGuard>
            </RequireRole>
          }
        />
        <Route
          path="/admin/tournaments/:id/setup/schedule"
          element={
            <RequireRole roles={["admin", "superadmin"]}>
              <TournamentSetupGuard setupOnly>
                <AdminTournamentSchedulePage setupMode />
              </TournamentSetupGuard>
            </RequireRole>
          }
        />
        <Route
          path="/admin/tournaments/:id/setup/settings"
          element={
            <RequireRole roles={["admin", "superadmin"]}>
              <TournamentSetupGuard setupOnly>
                <AdminTournamentCommunicationsPage setupMode />
              </TournamentSetupGuard>
            </RequireRole>
          }
        />
        <Route
          path="/admin/tournaments/:id"
          element={
            <RequireRole roles={["admin", "superadmin"]}>
              <TournamentSetupGuard>
                <AdminTournamentHomePage />
              </TournamentSetupGuard>
            </RequireRole>
          }
        />
        <Route
          path="/admin/players"
          element={
            <RequireRole roles={["admin", "superadmin"]}>
              <AdminPlayersListPage />
            </RequireRole>
          }
        />
        <Route
          path="/admin/team"
          element={
            <RequireRole roles={["admin", "superadmin"]}>
              <AdminTeamPage />
            </RequireRole>
          }
        />
        <Route
          path="/admin/players/:playerProfileId"
          element={
            <RequireRole roles={["admin", "superadmin"]}>
              <AdminPlayerProfilePage />
            </RequireRole>
          }
        />
        <Route
          path="/admin/calendar"
          element={
            <RequireRole roles={["admin", "superadmin"]}>
              <AdminClubCalendarPage />
            </RequireRole>
          }
        />
        <Route path="/admin/agenda" element={<Navigate to="/admin/calendar" replace />} />
        <Route
          path="/admin/tournaments/:id/settings"
          element={
            <RequireRole roles={["admin", "superadmin"]}>
              <TournamentSetupGuard>
                <AdminTournamentDetailPage />
              </TournamentSetupGuard>
            </RequireRole>
          }
        />
        <Route
          path="/admin/guia"
          element={
            <RequireRole roles={["admin", "superadmin"]}>
              <AdminGuidePage />
            </RequireRole>
          }
        />
        <Route
          path="/admin/tournaments/:id/team"
          element={
            <RequireRole roles={["admin", "superadmin"]}>
              <TournamentSetupGuard allowIncompleteAccess>
                <AdminTournamentTeamPage />
              </TournamentSetupGuard>
            </RequireRole>
          }
        />
        <Route
          path="/admin/tournaments/:id/players"
          element={
            <RequireRole roles={["admin", "superadmin"]}>
              <TournamentSetupGuard>
                <AdminTournamentPlayersPage />
              </TournamentSetupGuard>
            </RequireRole>
          }
        />
        <Route
          path="/admin/tournaments/:id/registrations"
          element={
            <RequireRole roles={["admin", "superadmin"]}>
              <TournamentSetupGuard allowIncompleteAccess>
                <AdminTournamentRegistrationsPage />
              </TournamentSetupGuard>
            </RequireRole>
          }
        />
        <Route
          path="/admin/tournaments/:id/registrations/cobrar"
          element={
            <RequireRole roles={["admin", "superadmin"]}>
              <TournamentSetupGuard allowIncompleteAccess>
                <AdminChargeCreatePage />
              </TournamentSetupGuard>
            </RequireRole>
          }
        />
        <Route
          path="/admin/tournaments/:id/players/new"
          element={
            <RequireRole roles={["admin", "superadmin"]}>
              <TournamentSetupGuard>
                <AdminTournamentPlayerEnrollPage />
              </TournamentSetupGuard>
            </RequireRole>
          }
        />
        <Route
          path="/admin/tournaments/:id/players/:participantId"
          element={
            <RequireRole roles={["admin", "superadmin"]}>
              {/* «Ver / editar» del paso Jugadores del asistente: abre aunque
                  el torneo siga en borrador (Eduardo 2026-10-02). */}
              <TournamentSetupGuard allowIncompleteAccess>
                <AdminTournamentPlayerDetailPage />
              </TournamentSetupGuard>
            </RequireRole>
          }
        />
        <Route
          path="/admin/tournaments/:id/groups"
          element={
            <RequireRole roles={["admin", "superadmin"]}>
              <TournamentSetupGuard>
                <AdminTournamentGroupsPage />
              </TournamentSetupGuard>
            </RequireRole>
          }
        />
        <Route
          path="/admin/tournaments/:id/rounds"
          element={
            <RequireRole roles={["admin", "superadmin"]}>
              <TournamentSetupGuard>
                <AdminTournamentRoundsPage />
              </TournamentSetupGuard>
            </RequireRole>
          }
        />
        <Route
          path="/admin/tournaments/:id/schedule"
          element={
            <RequireRole roles={["admin", "superadmin"]}>
              <TournamentSetupGuard>
                <AdminTournamentSchedulePage />
              </TournamentSetupGuard>
            </RequireRole>
          }
        />
        <Route
          path="/admin/tournaments/:id/calendar"
          element={<TournamentAliasRedirect target="schedule" />}
        />
        <Route
          path="/admin/tournaments/:id/results"
          element={
            <RequireRole roles={["admin", "superadmin"]}>
              <TournamentSetupGuard>
                <AdminTournamentSchedulePage resultsOnly />
              </TournamentSetupGuard>
            </RequireRole>
          }
        />
        <Route
          path="/admin/tournaments/:id/standings"
          element={
            <RequireRole roles={["admin", "superadmin"]}>
              <TournamentSetupGuard>
                <AdminTournamentStandingsPage />
              </TournamentSetupGuard>
            </RequireRole>
          }
        />
        <Route
          path="/admin/tournaments/:id/communications"
          element={
            <RequireRole roles={["admin", "superadmin"]}>
              <TournamentSetupGuard>
                <AdminTournamentCommunicationsPage />
              </TournamentSetupGuard>
            </RequireRole>
          }
        />
        <Route
          path="/admin/tournaments/:id/announcements"
          element={<TournamentAliasRedirect target="communications" />}
        />
        <Route
          path="/admin/tournaments/:id/rules"
          element={
            <RequireRole roles={["admin", "superadmin"]}>
              <TournamentSetupGuard>
                <AdminTournamentRulesPage />
              </TournamentSetupGuard>
            </RequireRole>
          }
        />
        <Route
          path="/admin/tournaments/:id/result-reports"
          element={
            <RequireRole roles={["admin", "superadmin"]}>
              <TournamentSetupGuard>
                <AdminTournamentResultReportsPage />
              </TournamentSetupGuard>
            </RequireRole>
          }
        />
        <Route
          path="/admin/tournaments/:id/referees"
          element={
            <RequireRole roles={["admin", "superadmin"]}>
              <TournamentSetupGuard>
                <AdminTournamentRefereesPage />
              </TournamentSetupGuard>
            </RequireRole>
          }
        />
        <Route
          path="/arbitro"
          element={
            <RequireRole roles={["admin"]}>
              <RefereeMatchesPage />
            </RequireRole>
          }
        />
        <Route
          path="/admin/notifications"
          element={
            <RequireRole roles={["admin", "superadmin"]}>
              <AdminNotificationsPage />
            </RequireRole>
          }
        />
        <Route
          path="/entrar"
          element={
            <RequireRole roles={["superadmin", "admin", "player", "club"]}>
              <ChooseAccountModePage />
            </RequireRole>
          }
        />
        <Route
          path="/player"
          element={
            <RequireRole roles={["player", "superadmin"]} allowPlayerProfile>
              <HomeJugadorPage />
            </RequireRole>
          }
        />
        <Route
          path="/player/profiles/:playerProfileId"
          element={
            <RequireRole roles={["player", "admin", "superadmin"]}>
              <PlayerProfilePage />
            </RequireRole>
          }
        />
        <Route
          path="/player/profile"
          element={
            <RequireRole roles={["player", "admin", "superadmin"]}>
              <PlayerProfilePage />
            </RequireRole>
          }
        />
        <Route
          path="/player/profile/results"
          element={
            <RequireRole roles={["player", "admin", "superadmin"]}>
              <PlayerHistoricalResultsPage />
            </RequireRole>
          }
        />
        <Route
          path="/player/friends"
          element={
            <RequireRole roles={["player", "admin", "superadmin"]}>
              <PlayerFriendsPage />
            </RequireRole>
          }
        />
        <Route
          path="/player/club"
          element={
            <RequireRole roles={["player", "admin", "superadmin"]}>
              <PlayerClubPage />
            </RequireRole>
          }
        />
        <Route
          path="/player/clubs"
          element={
            <RequireRole roles={["player", "admin", "superadmin"]}>
              <PlayerClubPage />
            </RequireRole>
          }
        />
        <Route
          path="/player/clubs/:clubId/reservations"
          element={
            <RequireRole roles={["player", "admin", "superadmin"]}>
              <PlayerClubReservationsPage />
            </RequireRole>
          }
        />
        <Route
          path="/player/tournaments"
          element={
            <RequireRole roles={["player", "admin", "superadmin"]}>
              <PlayerTournamentsPage />
            </RequireRole>
          }
        />
        <Route
          path="/player/result-reports"
          element={
            <RequireRole roles={["player", "admin", "superadmin"]}>
              <PlayerResultReportsPage />
            </RequireRole>
          }
        />
        <Route
          path="/player/documento"
          element={
            <RequireRole roles={["player", "admin", "superadmin"]}>
              <AdminDocumentViewerPage />
            </RequireRole>
          }
        />
        <Route
          path="/player/tournaments/:id"
          element={
            <RequireRole roles={["player", "admin", "superadmin"]}>
              <PlayerTournamentHomePage />
            </RequireRole>
          }
        />
        <Route
          path="/player/tournaments/:id/schedule"
          element={
            <RequireRole roles={["player", "admin", "superadmin"]}>
              <PlayerTournamentGamesPage viewMode="calendar" />
            </RequireRole>
          }
        />
        <Route
          path="/player/tournaments/:id/matchups"
          element={
            <RequireRole roles={["player", "admin", "superadmin"]}>
              <PlayerTournamentGamesPage viewMode="matchups" />
            </RequireRole>
          }
        />
        <Route
          path="/player/tournaments/:id/results"
          element={
            <RequireRole roles={["player", "admin", "superadmin"]}>
              <PlayerTournamentGamesPage viewMode="results" />
            </RequireRole>
          }
        />
        <Route
          path="/player/tournaments/:id/bracket"
          element={
            <RequireRole roles={["player", "admin", "superadmin"]}>
              <PlayerTournamentBracketPage />
            </RequireRole>
          }
        />
        <Route
          path="/player/tournaments/:id/sorteo"
          element={
            <RequireRole roles={["player", "admin", "superadmin"]}>
              <PlayerTournamentDrawCeremonyPage />
            </RequireRole>
          }
        />
        <Route
          path="/player/tournaments/:id/groups"
          element={
            <RequireRole roles={["player", "admin", "superadmin"]}>
              <PlayerStandingsPage groupsOnly />
            </RequireRole>
          }
        />
        <Route
          path="/player/tournaments/:id/standings"
          element={
            <RequireRole roles={["player", "admin", "superadmin"]}>
              <PlayerRankingPage />
            </RequireRole>
          }
        />
        <Route
          path="/player/tournaments/:id/credential"
          element={
            <RequireRole roles={["player", "admin", "superadmin"]}>
              <PlayerTournamentCredentialPage />
            </RequireRole>
          }
        />
        <Route
          path="/player/tournaments/:id/players"
          element={
            <RequireRole roles={["player", "admin", "superadmin"]}>
              <PlayerTournamentPlayersPage />
            </RequireRole>
          }
        />
        <Route
          path="/player/tournaments/:id/communications"
          element={
            <RequireRole roles={["player", "admin", "superadmin"]}>
              <PlayerCommunicationsPage />
            </RequireRole>
          }
        />
        <Route
          path="/player/communications"
          element={
            <RequireRole roles={["player", "admin", "superadmin"]}>
              <PlayerCommunicationsPage />
            </RequireRole>
          }
        />
        <Route
          path="/player/notifications"
          element={
            <RequireRole roles={["player", "admin", "superadmin"]}>
              <PlayerNotificationsPage />
            </RequireRole>
          }
        />
        <Route
          path="/player/tournaments/:id/rules"
          element={
            <RequireRole roles={["player", "admin", "superadmin"]}>
              <PlayerRulesPage />
            </RequireRole>
          }
        />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
